import { useGetCatalog, useGetLayerDocument } from "../../../api/generated/endpoints/generation/generation";
import { useMembership } from "../../../platform/membership";
import { LayerEditor } from "./LayerEditor";

export interface OpenLayerEditorProps {
  teamId: string;
  /** The Layerize's brand: whose products, generations and library "Add layer" offers. */
  brandId: string;
  /** The finished Layerize whose layers are edited. */
  jobId: string;
  onClose: () => void;
}

/**
 * Live's "Edit layers" (spec G13b): the editor on the Layerize's layer document, read when it
 * opens. The document is read once per opening: a read again would open it afresh and lose the
 * undo history, and closing forgets it, so the next opening signs its pictures anew. A Layerize
 * with no document (one from before its layers were kept) says live's failure words.
 */
export function OpenLayerEditor({ teamId, brandId, jobId, onClose }: OpenLayerEditorProps) {
  const viewer = useMembership(teamId)?.userId ?? null;
  // "Edit with AI" works where a model of the catalog serves the edit, as the Studio's tools.
  const aiEdits = useGetCatalog().data?.image_models.some((model) => model.job_types.includes("image.layer_edit")) === true;
  const layers = useGetLayerDocument(teamId, jobId, {
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });
  return (
    <LayerEditor
      jobId={jobId}
      viewer={viewer}
      library={{ teamId, brandId }}
      aiEdits={aiEdits}
      document={layers.data}
      // A retry shows the loading state while it reads.
      failed={layers.isError && !layers.isFetching}
      onRetry={() => void layers.refetch()}
      onClose={onClose}
    />
  );
}
